import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch, onMounted } from "vue";
import { useServices, types } from "@/lib/openapi";
import { RouteParamsGeneric, useRoute } from "vue-router";
import { useWorktreeStore } from "@/composables/worktree-manager";
import { SyncReturn, useSyncStore } from "./sync-store";
import { syncChannel } from "@/lib/broadcast-channels";

export type EntityInfo = types.EntityInfo;
export type EntityType = types.EntityType;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const EntityType = types.EntityType;
export type ProductInTree = types.ProductInTree;
export type ProcessInTree = types.ProcessInTree;

/**
 * Check if is valid EntityType key.
 */
function isEntityType(value: string): value is keyof typeof EntityType {
    return Object.keys(EntityType).includes(value);
}

/**
 * Convert a string into an EntityType.
 */
function getEntityType(value: string): EntityType {
    if (!isEntityType(value)) {
        throw new Error(`Invalid EntityType: ${value}`);
    }
    return EntityType[value];
}

/**
 * Gets the entityIds from the router.
 */
export function getEntityIds(
    params: RouteParamsGeneric
): [string, string | string[]][] {
    return Object.entries(params).filter(
        ([key, value]) => key.endsWith("Id") && value
    );
}

export const useEntityStore = (
    entityType?: EntityType,
    entityId?: string,
    isCurrentEntity: boolean = false
) => {
    // Create a unique store ID for this entity
    const storeId = entityId ? `entity-${entityId}` : "entity";

    return defineStore(storeId, () => {
        const entity = ref<EntityInfo | null>();
        const isFetching = ref(false);
        const api = useServices({
            requires: {
                auth: true,
                organization: true,
                stek: false,
            },
        });
        const { findDomainByIdentity, findProductByIdentity } =
            useWorktreeStore();

        /**
         * If the store is for the current entity.
         */
        if (isCurrentEntity) {
            const route = useRoute();

            watch(
                () => route.params,
                async (newParams) => {
                    if (newParams) {
                        const entityIds = getEntityIds(route.params);
                        if (entityIds.length === 0) {
                            entity.value = undefined;
                            return;
                        }

                        isFetching.value = true;
                        const [entityIdName, _entityId] =
                            entityIds[entityIds.length - 1];
                        const entityTypeKey = entityIdName.replace("Id", "");
                        entityType = getEntityType(entityTypeKey.toUpperCase());
                        entityId = `${_entityId}`;

                        try {
                            entity.value = await getEntity();
                        } catch (e) {
                            if (
                                e instanceof DOMException &&
                                e.name === "AbortError"
                            ) {
                                // Request was aborted by the AbortController.
                                return;
                            } else {
                                throw e;
                            }
                        } finally {
                            isFetching.value = false;
                        }
                    }
                },
                { immediate: true }
            );
        }

        const controller = ref(new AbortController());

        let lastFetch: string;
        let lastResult: EntityInfo | undefined;

        /**
         * Get the entity for the current store.
         */
        const getEntity = async (force: boolean = false) => {
            // Avoid fetching again if the same entity is already fetched. This prevents double fetches upon loading
            // the router the very first time.
            if (!force && lastFetch === `${entityId}-${entityType}`) {
                return lastResult;
            }
            isFetching.value = true;

            const path = {
                entity_id: `${entityId}`,
            } as types.StartBizEntitiesEntityTypeEntityIdInfoGetData["path"];
            if (entityType) {
                path.entity_type = entityType;
            }

            // Abort any previously running requests and create a new controller
            controller.value.abort();
            controller.value = new AbortController();

            lastFetch = `${entityId}-${entityType}`;
            try {
                const { data: _entity } =
                    await api.startBizEntitiesEntityTypeEntityIdInfoGet({
                        path,
                        signal: controller.value.signal,
                    });
                lastResult = _entity;
                return _entity;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetching.value = false;
            }
        };

        // Find a specific entity in the ancestors.
        const findType = ({
            entityType,
        }: {
            entityType: EntityType;
        }): EntityInfo | undefined => {
            if (!entity.value) {
                return;
            }

            let parent: EntityInfo | null | undefined = entity.value;
            while (parent) {
                if (parent.entity_type === entityType) {
                    return parent;
                }
                parent = parent.parent;
            }
            return;
        };

        // Add some entity information to the domain - later on we can return
        // this kind of stuff from the new info endpoint.
        interface DomainEntity extends EntityInfo {
            products: ProductInTree[];
        }
        const domain = computed<DomainEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const domain = findType({ entityType: EntityType.DOMAIN });
            if (!domain) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'products' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'products' store and we do not want to have to do
            // another request.
            const domainInWorktree = findDomainByIdentity(domain.identity);
            return {
                ...domain,
                products: domainInWorktree ? domainInWorktree.products : [],
            } as DomainEntity;
        });

        // Add some entity information to the product - later on we can return
        // this kind of stuff from the new info endpoint.
        interface ProductEntity extends EntityInfo {
            processes: ProcessInTree[];
        }
        const product = computed<ProductEntity | undefined>(() => {
            if (!entity.value) {
                return;
            }
            const product = findType({ entityType: EntityType.PRODUCT });
            if (!product) {
                return;
            }

            // Note that this is a temporary solution until we found a more rigid
            // way of adding extra information. The 'processes' mixed in here
            // are confusing as they add children to an entity with ancestors.
            // We should ideally only move in one direction (children OR ancestors)
            // to keep things clear. However this is temporarily necessary as
            // it is used by the 'processes' store and we do not want to have to
            // do another request.
            const productInWorktree = findProductByIdentity(product.identity);
            return {
                ...product,
                processes: productInWorktree ? productInWorktree.processes : [],
            };
        });

        const process = computed<EntityInfo | undefined>(() => {
            if (!entity.value) {
                return;
            }

            const process = findType({ entityType: EntityType.PROCESS });
            if (!process) {
                return;
            }

            return process;
        });

        const role = computed(() => {
            if (!entity.value) {
                return null;
            }

            const role = findType({ entityType: EntityType.ROLE });
            if (!role) {
                return null;
            }

            return role;
        });

        const task = computed(() => {
            if (!entity.value) {
                return;
            }
            return findType({ entityType: EntityType.TASK });
        });

        const gateway = computed(() => {
            if (!entity.value) {
                return;
            }
            return findType({ entityType: EntityType.GATEWAY });
        });

        const flow = computed(() => {
            if (!entity.value) {
                return;
            }
            return findType({ entityType: EntityType.FLOW });
        });

        const event = computed(() => {
            if (!entity.value) {
                return;
            }
            return findType({ entityType: EntityType.EVENT });
        });

        const artefact = computed(() => {
            if (!entity.value) {
                return;
            }
            return findType({ entityType: EntityType.ARTEFACT });
        });

        const findAncestorById = (identity: string): EntityInfo | undefined => {
            let currentEntity = entity.value;
            while (currentEntity) {
                if (identity === currentEntity.identity) {
                    return currentEntity;
                }
                currentEntity = currentEntity.parent;
            }
        };

        onMounted(async () => {
            // Actually get the Entity.
            entity.value = await getEntity();

            syncChannel.addEventListener(
                "message",
                async ({ data: { message, identity } }) => {
                    if (
                        message === "synced" &&
                        identity.attribute === "name" &&
                        findAncestorById(identity.entityId)
                    ) {
                        entity.value = await getEntity(true);
                    }
                }
            );
            useSyncStore().$onAction(({ name, after }) => {
                if (name === "sync") {
                    after(async (fullySyncedIdentities: SyncReturn) => {
                        if (
                            fullySyncedIdentities.some((identity) => {
                                if (identity.attribute !== "name") {
                                    return false;
                                }
                                return findAncestorById(identity.entityId);
                            })
                        ) {
                            entity.value = await getEntity(true);
                        }
                    });
                }
            });
        });

        return {
            entity,
            identity: computed(() => entity.value?.identity || "unknown"),
            name: computed(() => entity.value?.name || "unknown"),
            entityType: computed(() => entity.value?.entity_type || "unknown"),
            businessEntity: computed(() => {
                return findType({ entityType: EntityType.BUSINESS_ENTITY });
            }),
            domain,
            product,
            role,
            process,
            task,
            gateway,
            flow,
            event,
            artefact,
            stek: computed(() => {
                return findType({ entityType: EntityType.STEK });
            }),
            organization: computed(() => {
                return findType({ entityType: EntityType.ORGANIZATION });
            }),
            isFetching,
        };
    });
};

export const useEntityManager = (entityType: EntityType, entityId: string) =>
    storeToRefs(useEntityStore(entityType, entityId)());

// Helper to get store for current route's entity
export const useCurrentEntityStore = () => {
    return useEntityStore(undefined, undefined, true)();
};

export const useCurrentEntityManager = () =>
    storeToRefs(useCurrentEntityStore());
